import { useRef, useEffect, InputHTMLAttributes, useState } from "react";
import { validateNumber } from "../../../Utils/CommonFunctions";
import numberUtils from "../../../Utils/number.utils";

interface PriceInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  /** The numeric value to display */
  value?: string | number;
  /** Callback function that receives the numeric value */
  onChange?: (value: string | number) => void;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Maximum number of digits allowed (default: 7) */
  maxLength?: number;
  /** Additional CSS classes for the input */
  className?: string;
  /** Optional onFocus callback */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Optional onBlur callback */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Optional postfix */
  postfix?: string;
}

/**
 * PriceInput - A reusable price input component with thousand separators
 * 
 * This component formats numbers with thousand separators while maintaining
 * cursor position during editing. It returns clean numeric values to the
 * onChange handler.
 */
const PriceInput: React.FC<PriceInputProps> = ({
  value,
  onChange,
  placeholder = "Type amount",
  disabled = false,
  maxLength = 7,
  className = "",
  prefix = "",
  postfix = "",
  onFocus,
  onBlur,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const cursorPositionRef = useRef<number | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (cursorPositionRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
      cursorPositionRef.current = null;
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const cursorPos = input.selectionStart || 0;
    const oldValue = input.value;
    
    let numericValue = numberUtils.removeNonNumeric(e.target.value);
    
    if (numericValue.toString().length > maxLength) {
      return;
    }
    
    const oldValueBeforeCursor = oldValue.substring(0, cursorPos);
    const digitsBeforeCursor = oldValueBeforeCursor.replace(/,/g, '').length;
    
    const newFormattedValue = numberUtils.addCommas(numericValue);
    const newValueBeforeCursor = newFormattedValue.substring(
      0, 
      digitsBeforeCursor + (newFormattedValue.substring(0, digitsBeforeCursor).match(/,/g) || []).length
    );
    const newCommasBeforeCursor = (newValueBeforeCursor.match(/,/g) || []).length;
    
    const newCursorPos = digitsBeforeCursor + newCommasBeforeCursor;
    cursorPositionRef.current = newCursorPos;
    
    if (onChange) {
      onChange(validateNumber(numericValue));
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (onBlur) {
      onBlur(e);
    }
  };

  const valueWithCommas = value ? numberUtils.addCommas(value) : '0';
  const valueWithPrefix = prefix ? `${prefix} ${valueWithCommas}` : valueWithCommas;
  const displayValue = !isFocused && valueWithPrefix ? `${valueWithPrefix} ${postfix}` : valueWithPrefix;

  return (
    <input
      ref={inputRef}
      className={className}
      placeholder={placeholder}
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      {...rest}
      type="text"
    />
  );
};

export default PriceInput;

